import type { IPoiTranslation } from '@/shared/api/types/poi-types'
import { useTranslation } from 'react-i18next'
import { PoiTranslationSection } from '@/features/poi/poi-translation-section'
import { FormSectionCard } from '@/shared/ui/form-section-card'
import { SectionSaveButton } from '@/shared/ui/section-save-button'
import { TranslationsSkeleton } from '@/shared/ui/translations-skeleton'

interface TranslationsCardProps {
  isEdit: boolean
  isFormReady: boolean
  translations: IPoiTranslation[]
  onChange: (value: IPoiTranslation[]) => void
  /** Id the translation jobs are filed under (POI id, or the per-tenant draft id when creating). */
  entityId: string
  /** Language tab shown first. */
  initialLanguage: string
  /** Number of translation jobs that finished successfully but are not applied yet. */
  runningJobCount: number
  isSaving: boolean
  isSaveDisabled: boolean
  onSave: () => void
}

/** Name, description and audio script of the POI in every tenant language, with AI translation. */
export function TranslationsCard({
  isEdit,
  isFormReady,
  translations,
  onChange,
  entityId,
  initialLanguage,
  runningJobCount,
  isSaving,
  isSaveDisabled,
  onSave,
}: TranslationsCardProps) {
  const { t } = useTranslation()
  const entityLabel = translations.find((item) => item.name.trim())?.name ?? t('poi.title')
  const entityPath =
    typeof window === 'undefined'
      ? undefined
      : `${window.location.pathname}${window.location.search}`

  return (
    <FormSectionCard
      title={t('poi.translations')}
      action={
        isEdit && <SectionSaveButton disabled={isSaveDisabled} saving={isSaving} onSave={onSave} />
      }
    >
      <p className="mb-4 text-sm text-zinc-500">{t('poi.translationHint')}</p>
      {isFormReady ? (
        <>
          {runningJobCount > 0 && (
            <p className="mb-3 text-sm text-blue-700">
              {t('jobs.runningCount', { count: runningJobCount })}
            </p>
          )}
          <PoiTranslationSection
            translations={translations}
            onChange={onChange}
            entityId={entityId}
            entityLabel={entityLabel}
            initialActiveLanguage={initialLanguage}
            {...(entityPath ? { entityPath } : {})}
          />
        </>
      ) : (
        <TranslationsSkeleton />
      )}
    </FormSectionCard>
  )
}
